import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { AuthService } from '../../services/auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProfileCompletionModalComponent } from '../../modals/profile-completion-modal/profile-completion-modal.component';
import { Subscription } from 'rxjs';

import { logger } from '../../../helpers/logger';
@Component({
  selector: 'eng-main-layout',
  imports: [RouterOutlet, HeaderComponent, FooterComponent],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly modalService = inject(NgbModal);
  private authSubscription?: Subscription;
  private readonly dismissedKey = 'profileCompletionModalDismissed';

  ngOnInit(): void {
    // Subscribe to authentication changes
    this.authSubscription = this.authService.getAuthenticationStatus().subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.checkProfileCompletionAndShowModal();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  private checkProfileCompletionAndShowModal(): void {
    // Check if user has already dismissed the modal today
    const dismissedToday = this.hasUserDismissedModalToday();
    if (dismissedToday) {
      return;
    }

    this.authService.getUserState().subscribe({
      next: userState => {
        if (userState.isAuthenticated && userState.user && !userState.user.isProfileComplete) {
          // Small delay to ensure the layout is fully initialized
          setTimeout(() => {
            this.showProfileCompletionModal();
          }, 500);
        }
      },
      error: error => {
        logger.error('Error checking profile completion:', error);
      },
    });
  }

  private showProfileCompletionModal(): void {
    const modalRef = this.modalService.open(ProfileCompletionModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg', // Changed to large size to accommodate the form
    });

    modalRef.result.then(
      result => {
        // User submitted the form successfully or closed modal
        if (result === 'updated') {
          // Profile was updated successfully - no need to show modal again
          logger.debug('Profile updated successfully from modal');
        }
      },
      dismissReason => {
        if (dismissReason === 'remind-later') {
          // User clicked "Remind Me Later" - remember this for today
          this.setModalDismissedForToday();
        }
        // If user clicked close button or pressed escape, don't remember (they might want to see it again)
      }
    );
  }

  private hasUserDismissedModalToday(): boolean {
    const dismissed = localStorage.getItem(this.dismissedKey);
    if (!dismissed) {
      return false;
    }

    const dismissedDate = new Date(dismissed);
    const today = new Date();

    // Check if it's the same day
    return dismissedDate.toDateString() === today.toDateString();
  }

  private setModalDismissedForToday(): void {
    localStorage.setItem(this.dismissedKey, new Date().toISOString());
  }
}
